import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SquareCrossed,
} from '../../assets/js/svg/gui';
import {
  useTownAssets,
} from '../../stores';
import {
  imageSrcFromId,
} from '../../utils';
import { Button } from '../buttons';
import {
  CardTitle,
  Column,
  FullPopup,
  Row,
} from '../display';
import CardSeparator from '../display/CardSeparator';
import { SecuredImg } from '../graphics';
import Table from '../Table';

import TextInput from './TextInput';

function ImagePicker(props) {
  const {
    id,
    imagePickingDone,
    initialImageId,
    types,
    uploadType,
    wide,
    high,
    autoLabel, // If set, hides label input and auto-generates label with (x) suffix
    preview = true, // If set, show image thumbnail instead of button
  } = props;
  const { assets } = useTownAssets();
  const {
    translate,
    imagesList,
    fetchImagesList,
    uploadImage,
  } = assets;
  const [popupVisible, setPopupVisible] = useState(false);
  const [imageIdPicked, setImageIdPicked] = useState(initialImageId);
  const [newFile, setNewFile] = useState();
  const [newFileLabel, setNewFileLabel] = useState('');
  const [newFileSts, setNewFileSts] = useState('ready');

  const imagesFiltered = useMemo(
    () => {
      if (!imagesList) return undefined;

      return imagesList.filter((thisImage) => {
        if (!types) return true;

        return (types.includes(thisImage.type));
      });
    },
    [imagesList, types],
  );

  // Generate next available label with (x) suffix when autoLabel is set
  const getNextAvailableLabel = useCallback(
    (baseLabel) => {
      if (!imagesList) return baseLabel;

      const existingLabels = new Set(imagesList.map((img) => img.label));
      if (!existingLabels.has(baseLabel)) return baseLabel;

      let suffix = 1;
      while (existingLabels.has(`${baseLabel} (${suffix})`)) {
        suffix += 1;
      }
      return `${baseLabel} (${suffix})`;
    },
    [imagesList],
  );

  const imagePicked = useMemo(
    () => {
      if (!imagesFiltered) return undefined;

      return imagesFiltered.find((thisImage) => (thisImage.id === imageIdPicked));
    },
    [imageIdPicked, imagesFiltered],
  );

  const fileOnInput = useCallback(
    async (event) => {
      if (event?.target?.files) {
        const file = event.target.files[0];

        // https://stackoverflow.com/a/39485234/9316077
        // >>>> make sure we can select the same file twice
        event.target.value = null;

        setNewFile(file);
      }
    },
    [],
  );

  const addImage = useCallback(
    async () => {
      if (newFile) {
        setNewFileSts('busy');
        const label = autoLabel
          ? getNextAvailableLabel(autoLabel)
          : newFileLabel.trim();
        const fileInfo = {
          type: uploadType,
          label,
        };

        const newImageId = await uploadImage(newFile, fileInfo);

        if (newImageId !== undefined) {
          setImageIdPicked(newImageId);
          fetchImagesList();
          setNewFileSts('ready');
        } else {
          setNewFileSts('error');
        }
        setNewFile();
        setNewFileLabel('');
      }
    },
    [
      autoLabel, fetchImagesList, getNextAvailableLabel, newFile, newFileLabel,
      uploadType, uploadImage,
    ],
  );

  useEffect(
    () => {
      if (!imagesList) fetchImagesList();
    },
    [fetchImagesList, imagesList],
  );

  const handleClick = () => {
    setPopupVisible((current) => {
      if (!current) setImageIdPicked(initialImageId);
      return (!current);
    });
  };

  const cancel = () => {
    setPopupVisible(false);
    setNewFile();
    setNewFileLabel('');
    setNewFileSts('ready');
  };

  // const handleChange = (aNewColor) => {
  //   setImageIdPicked((currentimageIdPicked) => {
  //     if (currentimageIdPicked.r !== undefined) return aNewColor.rgb;

  //     return aNewColor.hex;
  //   });
  // };

  const initialImgSrc = imageSrcFromId(initialImageId);

  const imageIdSrc = imageSrcFromId(imageIdPicked);

  // const imagesOnClickColumn = (event) => {
  //   console.log(`event=${displayJson(event, 2)}`);
  // };

  const changeLabel = (event) => {
    setNewFileLabel(event.currentTarget.value);
  };

  const onClickRow = (rowData) => {
    setImageIdPicked(rowData.id);
  };

  // When autoLabel is set, label is always valid (auto-generated)
  const newFileLabelValid = autoLabel
    || (newFileLabel ? newFileLabel.trim().length > 2 : false);

  const popup = useMemo(
    () => {
      if (!popupVisible) return null;

      const imagesColumns = [
        {
          display: '#',
          id: 'cnt',
          renderRowCell: (rowData) => (rowData.cnt),
        },
        {
          display: 'Image',
          id: 'id',
          center: true,
          renderRowCell: (rowData) => (
            <SecuredImg
              urlPath={imageSrcFromId(rowData.id, true)}
              alt="?"
              className="h-10 w-10 object-contain pointer-events-none"
            />
          ),
        },
        {
          display: 'Label',
          id: 'label',
        },
        {
          display: 'Largeur',
          id: 'width',
        },
        {
          display: 'Hauteur',
          id: 'height',
        },
        {
          display: 'Modifié',
          id: 'updated',
        },
        {
          display: 'Fichier',
          id: 'filename',
        },
        {
          display: 'Type',
          id: 'type',
        },
      ];

      const done = () => {
        if (imagePickingDone) {
          imagePickingDone({
            id,
            imageId: imageIdPicked,
            label: imagePicked?.label,
            filename: imagePicked?.filename,
            width: imagePicked?.width,
            height: imagePicked?.height,
            filetype: imagePicked?.filetype,
            size: imagePicked?.size,
            scaleable: imagePicked?.filetype === 'svg',
          });
        }
        setPopupVisible(false);
        setNewFile();
        setNewFileLabel('');
        setNewFileSts('ready');
      };

      return (
        <FullPopup
          onClose={cancel}
          title="Choisissez une image"
          contentClassName="flex flex-col items-center overflow-hidden gap-6"
        >
          <Row>
            <Column framed className="h-48 w-48 shrink-0 p-1">
              <Column className="h-full w-full bg-checkers">
                {(imageIdPicked)
                  ? (
                      <SecuredImg
                        urlPath={imageIdSrc}
                        alt="logo"
                        className="h-full w-full object-contain"
                      />
                    )
                  : '+'}
              </Column>
            </Column>

            <Column start crossStart className="self-stretch">
              {
                (imagePicked)
                  ? (
                      <>
                        <span>{`"${imagePicked.label}"`}</span>
                        <span>{`Fichier: ${imagePicked.filename}`}</span>
                        <span>{`Résolution: ${imagePicked.width} x ${imagePicked.height} pixels`}</span>
                        <span>{`Type: ${imagePicked.filetype}`}</span>
                        <span>{`Taille: ${imagePicked.size} bits`}</span>
                      </>
                    )
                  : 'aucne sélection'
              }
            </Column>
          </Row>

          <Table
            className="flex-1"
            data={imagesFiltered}
            columns={imagesColumns}
            rowIdField="id"
            cellsClassName="text-sm"
            onClickRow={onClickRow}
            selectedId={imageIdPicked}
          />

          <CardSeparator horizontal />

          <CardTitle className="self-start" level={1} title="Ajouter une image" />

          <Column crossStart className="w-full">
            <Row>
              <Button
                label={translate('file')}
                id="selImage"
                fileOnInput={fileOnInput}
                disabled={newFileSts !== 'ready'}
                accept="image/bmp, image/gif, image/jpeg, image/png, image/svg+xml, image/tiff, image/webp, image/x-windows-bmp"
                baseColor
              />

              <span className="ml-2">{newFile?.name}</span>

              {/* When autoLabel is set, show Add button directly after file selection */}
              {(autoLabel && newFile) && (
                <Button
                  label={translate('add')}
                  id="addImage"
                  disabled={newFileSts === 'busy'}
                  onClick={addImage}
                  pending={newFileSts === 'busy'}
                  baseColor
                />
              )}
            </Row>

            {/* Show label input only when autoLabel is NOT set */}
            {(!autoLabel) && (
              <Row>
                Label:
                <TextInput
                  id="label"
                  name="label"
                  value={newFileLabel}
                  onChange={changeLabel}
                  invalid={newFile && (!newFileLabelValid)}
                />
                <Button
                  label={translate('add')}
                  id="addImage"
                  disabled={(newFileSts === 'busy') || (!newFileLabelValid)}
                  onClick={addImage}
                  pending={newFileSts === 'busy'}
                  baseColor
                />
              </Row>
            )}

            {((!autoLabel) && (!newFileLabelValid) && newFile)
              ? <i className="text-pxUiAlert">Veuillez nommer ce fichier</i>
              : null}

            { (newFile?.size > (1024 * 1024 * 4.9))
              ? <i className="text-pxUiAlert">Les fichiers de plus de 5Mo sont recompressés</i>
              : null}
          </Column>

          <Row fullw gap={8}>
            <Button
              label="Sélectionner"
              id="select"
              disabled={!(imageIdPicked)}
              onClick={done}
              baseColor
            />
          </Row>
          {/* </Column> */}
        </FullPopup>
      );
    },
    [
      addImage, autoLabel, fileOnInput, imageIdPicked, imageIdSrc,
      imagePicked, imagesFiltered, newFile, newFileLabel,
      newFileLabelValid, newFileSts, popupVisible, translate,
      id, imagePickingDone,
    ],
  );

  return (
    <div className="relative">

      {(preview)
        ? (
            <button
              className={classNames(
                'p-0.5 border border-pxOutlineStrong rounded-sm cursor-pointer relative shrink-0 bg-checkers bg-[size:8px]',
                {
                  'w-16 h-8': wide,
                  'w-8 h-12': wide,
                  'w-10 h-10': (!wide) && (!high),
                },
              )}
              onClick={handleClick}
              type="button"
            >
              {(initialImageId)
                ? (
                    <SecuredImg
                      urlPath={initialImgSrc}
                      alt="logo"
                      className="h-full w-full object-contain shrink-0"
                    />
                  )
                : <SquareCrossed className="h-full w-full object-contain shrink-0" />}
            </button>
          )
        : (
            <Button
              label={(initialImageId) ? translate('edit') : translate('add')}
              onClick={handleClick}
              baseColor
            />
          )}

      { popup}

    </div>
  );
}

ImagePicker.propTypes = {
  id: PropTypes.string,
  imagePickingDone: PropTypes.func,
  initialImageId: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.string),
  uploadType: PropTypes.string,
  wide: PropTypes.bool,
  high: PropTypes.bool,
  autoLabel: PropTypes.string,
  preview: PropTypes.bool,
};

export default ImagePicker;
